import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlexusBackground from "../components/PlexusBackground";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import SmartLink from "../components/SmartLink";
import PlatformLogo from "../components/PlatformLogo";
import { apiGet, formatProductForDisplay } from "../api/client";
import { useTwitchLive } from "../hooks/useTwitchLive";
import { mergeHomeContent } from "../data/homeDefaults";
import {
  HomeButtons,
  ToolsSection,
  UpdatesSection,
  VideosSection,
  ReviewsSection,
  CtaSection,
  CustomSection,
} from "./home/HomeSections";
import "../styles.css";

function useApi(path, fallback = []) {
  const [state, setState] = useState({ data: fallback, loading: Boolean(path), error: null });
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    apiGet(path)
      .then((data) => !cancelled && setState({ data, loading: false, error: null }))
      .catch((e) => {
        console.error(`Failed to fetch ${path}:`, e);
        if (!cancelled) setState({ data: fallback, loading: false, error: e.message });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  return state;
}

export default function Home() {
  const { isLive, loading: twitchLoading } = useTwitchLive("zemaxi");

  const page = useApi("/pages/home", null);
  const content = useMemo(() => mergeHomeContent(page.data?.content), [page.data]);
  const hero = content.hero;
  const sectionOf = (id) => content.sections.find((s) => s.id === id);
  const countOf = (id) => Math.max(1, Number(sectionOf(id)?.count) || 3);

  const products = useApi("/products");
  const updates = useApi(`/updates?limit=${countOf("updates")}`);
  const videos = useApi(`/videos?type=video&limit=${countOf("videos")}`);
  const featuredVideos = useApi("/videos?featured=1&limit=1");
  const reviews = useApi(`/reviews?limit=${countOf("reviews")}&excludeUpcoming=1`);

  // Featured first, each group in catalog order (the API already sorts by sort_order).
  const rankedProducts = useMemo(() => {
    const all = products.data.map(formatProductForDisplay);
    return [...all.filter((p) => p.featured), ...all.filter((p) => !p.featured)];
  }, [products.data]);

  const latestUpdate = updates.data[0];
  const platforms = hero.platforms || [];
  const announceHref = latestUpdate?.link || latestUpdate?.product?.link || "/updates";
  // Spotlight card goes in front, so it is rendered last.
  const tickerIds = hero.tickerProductIds || [];
  const tickerProducts = tickerIds.length
    ? tickerIds.map((id) => rankedProducts.find((p) => p.id === id)).filter(Boolean)
    : rankedProducts;
  const showcase = rankedProducts.filter((p) => p.featured && p.img).slice(0, 3).reverse();

  const renderSection = (section) => {
    if (!section.enabled) return null;
    if (section.custom) return <CustomSection key={section.id} section={section} />;
    switch (section.id) {
      case "tools":
        return (
          <ToolsSection
            key="tools"
            section={section}
            products={rankedProducts.slice(0, countOf("tools"))}
            loading={products.loading}
            error={products.error && "Failed to load tools. Is the CMS API running?"}
          />
        );
      case "updates":
        return <UpdatesSection key="updates" section={section} updates={updates.data} loading={updates.loading} error={updates.error} />;
      case "videos":
        return (
          <VideosSection
            key="videos"
            section={section}
            featured={featuredVideos.data[0]}
            videos={videos.data}
            loading={videos.loading}
            error={videos.error && `Failed to load videos: ${videos.error}`}
          />
        );
      case "reviews":
        return <ReviewsSection key="reviews" section={section} reviews={reviews.data} loading={reviews.loading} />;
      case "cta":
        return <CtaSection key="cta" section={section} />;
      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title={page.data?.title || "Home"}
        description={page.data?.seo?.description || "Welcome to ZeMaxi's digital hub! Watch live Twitch streams, explore free streaming tools for OBS Studio and Streamer.bot, and discover content that will elevate your streaming setup."}
        keywords={page.data?.seo?.keywords || "ZeMaxi, Twitch streamer, streaming tools, OBS overlays, Streamer.bot, free stream tools, gaming streamer, live stream, content creator"}
        url={page.data?.seo?.url || "https://zemaxi.com/"}
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="hero">
          <div className="container hero-layout">
            <div className="hero-content">
              <SmartLink
                href={isLive ? "https://twitch.tv/zemaxi" : announceHref}
                className={`hero-pill font-mono${isLive ? " is-live" : ""}`}
              >
                <span className="hero-pill__status">
                  <span className="hero-pill__dot" aria-hidden="true" />
                  {twitchLoading ? "Sync" : isLive ? "Live now" : "Offline"}
                </span>
                {isLive ? (
                  <span>Watch on Twitch</span>
                ) : latestUpdate ? (
                  <span>New: {latestUpdate.product?.name || latestUpdate.title}</span>
                ) : (
                  <span>{rankedProducts.length} tools</span>
                )}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </SmartLink>
              <h1>
                {hero.title} {hero.highlight && <span className="hero-accent">{hero.highlight}</span>}
                {platforms.length > 0 && (
                  <span className="hero-platforms">
                    {hero.platformsPrefix && <span>{hero.platformsPrefix} </span>}
                    {platforms.map((platform, index) => (
                      <React.Fragment key={platform}>
                        <PlatformLogo platform={platform} />
                        <span className="visually-hidden">
                          {index < platforms.length - 2 ? ", " : index === platforms.length - 2 ? " and " : ""}
                        </span>
                      </React.Fragment>
                    ))}
                  </span>
                )}
              </h1>
              {hero.subtitle && <p>{hero.subtitle}</p>}
              <HomeButtons buttons={hero.buttons} />
            </div>
            {showcase.length > 0 && (
              <div className={`hero-showcase hero-showcase--${showcase.length}`}>
                {showcase.map((product, index) => (
                  <SmartLink
                    key={product.id}
                    href={product.link}
                    className={`hero-showcase__card hero-showcase__card--${index}`}
                    aria-label={product.title}
                  >
                    <img src={product.img} alt="" />
                    <span className="hero-showcase__name font-mono">{product.title}</span>
                  </SmartLink>
                ))}
              </div>
            )}
          </div>
          <div className="hero-background">
            <PlexusBackground />
          </div>
        </section>

        {hero.showTicker && tickerProducts.length > 0 && (
          <div className="home-ticker" aria-label="Tools">
            <div className="home-ticker__track">
              {[0, 1].map((copy) => (
                <ul key={copy} aria-hidden={copy === 1 ? "true" : undefined}>
                  {tickerProducts.map((product) => (
                    <li key={product.id}>
                      <SmartLink href={product.link} tabIndex={copy === 1 ? -1 : undefined}>
                        <img src={product.img} alt="" loading="lazy" />
                        {product.title}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        )}

        {content.sections.map(renderSection)}
      </main>
      <Footer />
    </>
  );
}
